import type { CartItem } from "../context/cartContext";
import { cn } from "../lib/utils";
import { formatPrice } from "../lib/helpers/storehelpers";
import {
  hiddenPriceUnits,
  isHiddenPriceCartItem,
  sumPricedLines,
} from "../lib/helpers/cartDisplay";

/** Title case — product pages and per-line cart / mini-cart price labels. */
export const HIDDEN_CART_PRICE_LABEL = "Contact Supplier for Pricing";

/** Lowercase — subtotal footnote and checkout/confirmation disclaimers only. */
export const HIDDEN_CART_PRICE_LABEL_SENTENCE = "contact supplier for pricing";

type PricedRow = Pick<CartItem, "price" | "quantity">;

export function CartLineExtensionAmount({
  item,
  className,
}: {
  item: PricedRow;
  /** Applied when the line has a known dollar amount */
  className?: string;
}) {
  if (isHiddenPriceCartItem(item)) {
    return (
      <span className="text-xs font-normal text-gray-500 dark:text-gray-400 leading-snug text-right max-w-[10rem]">
        {HIDDEN_CART_PRICE_LABEL}
      </span>
    );
  }
  return (
    <span
      className={
        className ?? "text-sm font-semibold text-gray-900 dark:text-gray-100"
      }
    >
      ${formatPrice(item.price * item.quantity)}
    </span>
  );
}

export function CartUnitPriceLabel({
  item,
  className,
}: {
  item: PricedRow;
  /** Applied when the line has a known unit price */
  className?: string;
}) {
  if (isHiddenPriceCartItem(item)) {
    return (
      <span className="text-xs text-gray-500 leading-snug">
        {HIDDEN_CART_PRICE_LABEL}
      </span>
    );
  }
  return (
    <span className={className ?? "text-xs text-gray-500"}>
      ${formatPrice(item.price)}/unit
    </span>
  );
}

export function CartLineTotalRight({
  item,
  className,
}: {
  item: PricedRow;
  /** Applied when the line has a known dollar total */
  className?: string;
}) {
  if (isHiddenPriceCartItem(item)) {
    return (
      <div className="text-sm font-medium text-gray-500 text-right max-w-[11rem] leading-snug">
        {HIDDEN_CART_PRICE_LABEL}
      </div>
    );
  }
  return (
    <div className={className ?? "text-sm font-medium"}>
      ${formatPrice(item.price * item.quantity)}
    </div>
  );
}

/** Same wording as cart subtotal footer for hidden-price units. */
export function HiddenPriceUnitsNote({
  hiddenUnits,
  pricedSubtotal,
  className,
}: {
  hiddenUnits: number;
  pricedSubtotal: number;
  className?: string;
}) {
  if (hiddenUnits <= 0) return null;
  return (
    <p className={cn("text-xs text-gray-500 leading-snug", className)}>
      {`${pricedSubtotal > 0 ? "+ " : ""}${hiddenUnits} ${
        hiddenUnits === 1 ? "item" : "items"
      } — ${HIDDEN_CART_PRICE_LABEL_SENTENCE}`}
    </p>
  );
}

/**
 * Mini-cart / dropdown / order-summary totals.
 * All priced → single "Subtotal" row. Any hidden-price row → priced subtotal + note, no misleading grand total.
 */
export function CartPricingFooter({
  items,
  className,
  amountClassName = "font-semibold",
  labelAllPriced = "Subtotal",
}: {
  items: CartItem[];
  className?: string;
  amountClassName?: string;
  /** e.g. "Subtotal:" on full cart page */
  labelAllPriced?: string;
}) {
  if (items.length === 0) return null;

  const priced = sumPricedLines(items);
  const hiddenUnits = hiddenPriceUnits(items);
  const hasHidden = hiddenUnits > 0;
  const allPriced = !hasHidden;

  if (allPriced) {
    return (
      <div
        className={`flex items-center justify-between ${className ?? ""}`.trim()}
      >
        <span className="font-semibold">{labelAllPriced}</span>
        <span className={amountClassName}>${formatPrice(priced)}</span>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className ?? ""}`.trim()}>
      {priced > 0 && (
        <div className="flex justify-between items-center gap-2">
          <span className="font-semibold text-gray-700">Subtotal:</span>
          <span className={amountClassName}>${formatPrice(priced)}</span>
        </div>
      )}
      <HiddenPriceUnitsNote hiddenUnits={hiddenUnits} pricedSubtotal={priced} />
    </div>
  );
}
